import { useAutoFitPdfHeight } from "@/components/Pdf/useAutoFitPdfHeight";
import { usePdfViewer } from "@/components/Pdf/usePdfViewer";
import { BodyText, Button, Callout, Skeleton } from "@/components/ui";
import { formatDateRange } from "@/lib/time";
import { useS3Download } from "@/network/useS3Download";
import { useParams } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  ExternalLink,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Document, Page } from "react-pdf";
import type { WorkNoteItem } from "./types";

interface WorkNotePdfViewerProps {
  workNote: WorkNoteItem;
  onBack: () => void;
}

export function WorkNotePdfViewer({
  workNote,
  onBack,
}: WorkNotePdfViewerProps) {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data, isLoading } = useS3Download({
    patientId,
    path: workNote.pdfS3Key ?? "",
    enabled: Boolean(workNote.pdfS3Key),
  });

  const { containerRef, containerWidth, pageHeight, remeasure } =
    useAutoFitPdfHeight({
      bottomMarginPx: 128,
      minHeight: 384,
    });

  const [src, setSrc] = useState("");

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      setSrc(url);
      remeasure();
      return () => URL.revokeObjectURL(url);
    }
    setSrc("");
  }, [data, remeasure]);

  const {
    currentPage,
    numPages,
    scale,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    zoomIn,
    zoomOut,
    resetZoom,
    openInNewTab,
    setPageRef,
  } = usePdfViewer({
    src,
    containerRef,
  });

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          leftIcon={ArrowLeft}
        >
          All Work Notes
        </Button>
      </div>

      <div className="flex flex-col gap-1 mb-3">
        <BodyText size="sm" className="font-medium">
          {workNote.reason}
        </BodyText>
        <BodyText size="xs" textColor="tertiary">
          Issued {format(parseISO(workNote.dateIssued), "MM/dd/yyyy")}
          {workNote.excuseStartDate && workNote.excuseEndDate && (
            <>
              {" \u2022 "}
              {formatDateRange(
                workNote.excuseStartDate,
                workNote.excuseEndDate,
              )}
            </>
          )}
        </BodyText>
      </div>

      {!workNote.pdfS3Key && (
        <Callout
          variant="info"
          title="PDF not yet available"
          description="The PDF for this work note has not been generated yet."
        />
      )}

      {workNote.pdfS3Key && isLoading && (
        <div className="w-full bg-secondary mt-3 flex justify-center items-start min-h-0">
          <div className="relative w-full" style={{ height: pageHeight }}>
            <div ref={containerRef} className="w-full h-full overflow-auto p-4">
              <Skeleton
                style={{
                  width: Math.floor(containerWidth * scale),
                  height: `calc(${pageHeight}px - 48px)`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {workNote.pdfS3Key && !isLoading && !data && (
        <Callout
          className="mt-2"
          variant="danger"
          title="Something went wrong!"
          description="Failed to load the PDF."
        />
      )}

      {workNote.pdfS3Key && data && (
        <div className="w-full bg-secondary mt-3 flex justify-center items-start min-h-0">
          <div className="relative w-full" style={{ height: pageHeight }}>
            <div className="absolute right-3 top-3 z-10">
              <Button
                variant="secondary"
                size="md"
                onClick={() => openInNewTab()}
                className="shadow-md"
                leftIcon={ExternalLink}
              >
                Open in New Tab
              </Button>
            </div>
            {numPages ? (
              <div className="absolute left-3 top-3 z-10 bg-black/70 px-3 py-1.5 rounded-lg">
                <BodyText size="sm" textColor="white">
                  {`Page ${currentPage} of ${numPages}`}
                </BodyText>
              </div>
            ) : null}
            {scale !== 1.0 && (
              <div className="absolute right-3 bottom-16 z-10 bg-black/70 px-3 py-1.5 rounded-lg">
                <BodyText
                  size="sm"
                  textColor="white"
                >{`${(scale * 100).toFixed(0)}%`}</BodyText>
              </div>
            )}
            <div className="absolute right-3 bottom-3 z-10 flex gap-2">
              {scale !== 1.0 && (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => resetZoom()}
                  className="shadow-lg"
                  aria-label="Reset zoom"
                >
                  <RotateCcw className="w-5 h-5 text-button-secondary" />
                </Button>
              )}
              <Button
                variant="primary"
                size="icon"
                onClick={() => zoomOut()}
                className="shadow-lg"
                aria-label="Zoom out"
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </Button>
              <Button
                variant="primary"
                size="icon"
                onClick={() => zoomIn()}
                className="shadow-lg"
                aria-label="Zoom in"
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </Button>
            </div>
            <div ref={containerRef} className="w-full h-full overflow-auto p-4">
              <div
                className="mx-auto"
                style={{ width: Math.floor(containerWidth * scale) }}
              >
                <Document
                  file={src}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <Skeleton
                      style={{
                        width: Math.floor(containerWidth * scale),
                        height: `calc(${pageHeight}px - 48px)`,
                      }}
                    />
                  }
                  className="flex flex-col"
                >
                  {numPages &&
                    containerWidth > 0 &&
                    Array.from({ length: numPages }, (_, i) => (
                      <div
                        key={`page_wrap_${i + 1}`}
                        ref={setPageRef(i)}
                        className="mb-4 last-of-type:mb-0 shadow-lg"
                      >
                        <Page
                          pageNumber={i + 1}
                          width={Math.floor(containerWidth * scale)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={null}
                        />
                      </div>
                    ))}
                </Document>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
